import React, { useState, useEffect, useCallback } from 'react';
import { Music, Send, Copy, Check, Code, Sliders, ToggleLeft, ToggleRight, AlertTriangle, Sparkles, Scissors } from 'lucide-react';
import { Modal } from '@components/ui';
import { copyToClipboard } from '@/utils/clipboard';
import { SUNO_GENERATION_MODELS, DEFAULT_SUNO_MODEL, resolveSunoGenerationModel, isCustomModelMv, pillIdForGeneration } from '@/shared/sunoModels';
import { extractSunoModels } from '@/shared/sunoModelList';
import { getSunoCredits } from '@services/sunoApi';
import { SUNO_LYRICS_INJECTION_LINES } from '@/shared/sunoLyricsInjection';
import { trimPhysicsBlocksToFit } from '@/shared/physicsHardCap';
import type { SunoBilling } from '@/hooks/useSunoIntegration';

export function generateSunoConsoleCode(lyrics: string, style: string, title: string, styleStrength: number, weirdness: number): string {
  return [
    '// --- HookHouse → Suno Console Injection ---',
    '// Paste this into suno.com/create browser console in "Custom Mode"',
    '',
    'function setNativeValue(element, value) {',
    '    if (!element) return;',
    '    const valueSetter = Object.getOwnPropertyDescriptor(element.constructor.prototype, "value")?.set;',
    '    const prototypeValueSetter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(element), "value")?.set;',
    '    if (valueSetter && valueSetter !== prototypeValueSetter) {',
    '        valueSetter.call(element, value);',
    '    } else if (valueSetter) {',
    '        valueSetter.call(element, value);',
    '    }',
    '    element.dispatchEvent(new Event("input", { bubbles: true }));',
    '}',
    '',
    ...SUNO_LYRICS_INJECTION_LINES,
    '',
    'function adjustSlider(sliderLabel, targetValue) {',
    '    const slider = document.querySelector(`div[role="slider"][aria-label*="${sliderLabel}"]`);',
    '    if (slider) {',
    '        slider.focus();',
    '        const pressKey = (key) => slider.dispatchEvent(new KeyboardEvent("keydown", { key, code: key, bubbles: true, cancelable: true }));',
    '        const intervalId = setInterval(() => {',
    '            const currentVal = parseFloat(slider.getAttribute("aria-valuenow"));',
    '            if (Math.abs(currentVal - targetValue) < 1) { clearInterval(intervalId); return; }',
    '            if (currentVal > targetValue) pressKey("ArrowLeft"); else if (currentVal < targetValue) pressKey("ArrowRight");',
    '        }, 10);',
    '        console.log("✓ " + sliderLabel + " → " + targetValue);',
    '    } else {',
    '        console.warn("⚠ Slider not found: " + sliderLabel);',
    '    }',
    '}',
    '',
    'console.log("🚀 Injecting from HookHouse...");',
    '',
    '// Collect all textareas in DOM order (the style box is the first one that is not the legacy lyrics textarea)',
    'const _allTextareas = Array.from(document.querySelectorAll(\'textarea\'));',
    '',
    '// Lyrics: the lyrics box is a Lexical editor, filled by injectLyrics (runs alongside the fields below)',
    'const lyricsBlock = document.querySelector(\'textarea[placeholder*="Write some lyrics"]\'); // legacy layout only; also kept out of the style lookup',
    'if (lyricsBlock) { setNativeValue(lyricsBlock, ' + JSON.stringify(lyrics.substring(0, 5000)) + '); console.log("✓ Lyrics injected (legacy textarea)"); }',
    'else { injectLyrics(' + JSON.stringify(lyrics.substring(0, 5000)) + '); }',
    '',
    '// Style — placeholder variants first, then first textarea that is NOT the lyrics one',
    'const styleInput =',
    '  document.querySelector(\'input[placeholder*="style of music"]\') ||',
    '  document.querySelector(\'input[placeholder*="Style"]\') ||',
    '  document.querySelector(\'input[placeholder*="genre"]\') ||',
    '  document.querySelector(\'input[placeholder*="Enter style"]\') ||',
    '  document.querySelector(\'input[placeholder*="Describe"]\') ||',
    '  document.querySelector(\'input[placeholder*="Tags"]\') ||',
    '  document.querySelector(\'textarea[placeholder*="style"]\') ||',
    '  document.querySelector(\'textarea[placeholder*="Style"]\') ||',
    '  _allTextareas.find(t => t !== lyricsBlock) ||',
    '  null;',
    'if (styleInput) { setNativeValue(styleInput, ' + JSON.stringify(style.substring(0, 1000)) + '); console.log("✓ Style injected into:", styleInput.placeholder || styleInput.getAttribute(\'aria-label\') || "(textarea #" + _allTextareas.indexOf(styleInput) + ")"); }',
    'else { const inps = Array.from(document.querySelectorAll(\'input[type="text"],input:not([type]),textarea\')); console.warn("⚠ Style field not found. Inputs/textareas:", inps.map(i => i.placeholder || i.getAttribute(\'aria-label\') || \'(none)\')); }',
    '',
    '// Title',
    'const songTitleInput = document.querySelector(\'input[placeholder*="Song Title"]\') || document.querySelector(\'input[placeholder*="title"]\');',
    'if (songTitleInput) { setNativeValue(songTitleInput, ' + JSON.stringify(title.substring(0, 80)) + '); console.log("✓ Title injected"); }',
    'else { console.warn("⚠ Title field not found"); }',
    '',
    '// Intelligence Sliders',
    'adjustSlider("Weirdness", ' + weirdness + ');',
    'adjustSlider("Style Influence", ' + styleStrength + ');',
    '',
    'console.log("%c Injection Complete! Ready to Create. ", "background: rgb(var(--color-accent)); color: #000; font-size: 14px; font-weight: bold;");'
  ].join('\n');
}

/** Suno's Variety stops, indexed by the aug_creativity value each one sends. */
const VARIETY_LABELS = ['Off', 'Normal', 'High', 'Extra', 'Max'];

const fmtDuration = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

export interface SunoPrepPayload {
  title: string;
  tags: string;
  negativeTags: string;
  lyrics: string;
  styleStrength: number;
  weirdness: number;
  makeInstrumental: boolean;
  /** Suno's Vocal Gender toggle ("m" / "f"); null = leave to Suno. Per send, not saved. */
  vocalGender?: 'm' | 'f' | null;
  /** Suno's Max Mode toggle. Per send, not saved. */
  maxMode?: boolean;
  /** Suno's Variety slider: 0 Off, 1 Normal, 2 High, 3 Extra, 4 Max (its aug_creativity value). Per send, not saved. */
  variety?: number;
  /** Suno's Duration > Custom in seconds (10-360); null/unset = Auto. Per send, not saved. */
  durationSec?: number | null;
  /** Suno's Personalize (My Taste) toggle. Per send, not saved. */
  personalize?: boolean;
  /** Suno model to generate with (a codename from SUNO_GENERATION_MODELS); unset = the saved setting. */
  model?: string;
  /** The Vault pill this model adds to the song once it is sent (null when it has none). */
  modelPill?: string | null;
  draftId?: string | null;
}

interface SunoPrepModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (payload: SunoPrepPayload) => void;
  initialPayload?: Partial<SunoPrepPayload>;
  isGenerating?: boolean;
  billing?: SunoBilling | null;
  onUpsample?: (tags: string) => Promise<string | null>;
}

const LIMITS = {
  title: 80,
  tags: 1000,
  negativeTags: 1000,
  lyrics: 5000,
};

const savedSunoModel = (): string | null => {
  try { return localStorage.getItem('suno_model'); } catch { return null; }
};

const savedSunoToken = (): string => {
  try { return localStorage.getItem('suno_cookie') || ''; } catch { return ''; }
};

interface CustomModelChoice { mv: string; name: string }

const CharCount: React.FC<{ current: number; max: number }> = ({ current, max }) => {
  const over = current > max;
  return (
    <span className={`text-[10px] font-mono tabular-nums ${over ? 'text-red-400 font-bold' : 'text-slate-500'}`}>
      {current}/{max}
    </span>
  );
};

const SunoPrepModal: React.FC<SunoPrepModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  initialPayload,
  isGenerating = false,
  billing,
  onUpsample,
}) => {
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [negativeTags, setNegativeTags] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [styleStrength, setStyleStrength] = useState(85);
  const [weirdness, setWeirdness] = useState(65);
  const [makeInstrumental, setMakeInstrumental] = useState(false);
  const [vocalGender, setVocalGender] = useState<'m' | 'f' | null>(null);
  const [maxMode, setMaxMode] = useState(false);
  const [variety, setVariety] = useState(0);
  const [durationSec, setDurationSec] = useState<number | null>(null);
  const [personalize, setPersonalize] = useState(false);
  const [model, setModel] = useState(DEFAULT_SUNO_MODEL);
  const [customModels, setCustomModels] = useState<CustomModelChoice[]>([]);
  const [customLoadFailed, setCustomLoadFailed] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [isUpsampling, setIsUpsampling] = useState(false);
  const [trimNote, setTrimNote] = useState<string | null>(null);

  const handleTrimToFit = useCallback(() => {
    const result = trimPhysicsBlocksToFit(lyrics, LIMITS.lyrics);
    if (!result.trimmed) {
      setTrimNote(result.stillOverBy > 0
        ? "Couldn't find physics blocks to trim — the overage is in your actual lyrics/tags."
        : 'Nothing to trim.');
      return;
    }
    setLyrics(result.text);
    setTrimNote(result.stillOverBy > 0
      ? `Dropped ${result.clausesDropped} physics clause${result.clausesDropped === 1 ? '' : 's'} — still ${result.stillOverBy} over (overage is in your lyrics, not physics).`
      : `Dropped ${result.clausesDropped} physics clause${result.clausesDropped === 1 ? '' : 's'} to fit.`);
  }, [lyrics]);

  // Populate fields when modal opens with new payload
  useEffect(() => {
    if (isOpen && initialPayload) {
      setTitle(initialPayload.title || '');
      setTags(initialPayload.tags || '');
      setNegativeTags(initialPayload.negativeTags || '');
      setLyrics(initialPayload.lyrics || '');
      setStyleStrength(initialPayload.styleStrength ?? 85);
      setWeirdness(initialPayload.weirdness ?? 65);
      setMakeInstrumental(initialPayload.makeInstrumental ?? false);
      setVocalGender(initialPayload.vocalGender ?? null);
      setMaxMode(initialPayload.maxMode ?? false);
      setVariety(initialPayload.variety ?? 0);
      setDurationSec(initialPayload.durationSec ?? null);
      setPersonalize(initialPayload.personalize ?? false);
      setModel(resolveSunoGenerationModel(initialPayload.model, savedSunoModel()));
      setCodeCopied(false);
    }
  }, [isOpen, initialPayload]);

  // Your custom models come from your Suno account's model list (cached for a few minutes server-side).
  // Best effort: with no token, or if the list cannot be read, the v6 pills still work.
  useEffect(() => {
    if (!isOpen) return;
    const token = savedSunoToken();
    if (!token) { setCustomModels([]); setCustomLoadFailed(false); return; }
    let cancelled = false;
    getSunoCredits(token)
      .then(json => {
        if (cancelled) return;
        const mine = extractSunoModels(json)
          .filter(m => m.isCustom && m.canUse !== false && isCustomModelMv(m.externalKey))
          .map(m => ({ mv: m.externalKey, name: m.name }));
        setCustomModels(mine);
        setCustomLoadFailed(false);
      })
      .catch(() => { if (!cancelled) { setCustomModels([]); setCustomLoadFailed(true); } });
    return () => { cancelled = true; };
  }, [isOpen]);

  const isOverLimit =
    title.length > LIMITS.title ||
    tags.length > LIMITS.tags ||
    negativeTags.length > LIMITS.negativeTags ||
    lyrics.length > LIMITS.lyrics;

  // A custom model has a default style of its own, and suno.com lets you leave Style blank to use it.
  // Standard models still need style text.
  const isCustomSelected = isCustomModelMv(model);
  const customName = customModels.find(c => c.mv === model)?.name;
  const canGenerate = !isGenerating && !isOverLimit && (lyrics.trim() || makeInstrumental) && (tags.trim() || isCustomSelected);

  // Why Send is gray, in plain words, so a disabled button is never a mystery.
  let blockedReason: string | null = null;
  if (!isGenerating && !canGenerate) {
    if (isOverLimit) blockedReason = 'Something is over its character limit (see the red counts above).';
    else if (!lyrics.trim() && !makeInstrumental) blockedReason = 'Add lyrics, or turn on Instrumental Only.';
    else if (!tags.trim() && !isCustomSelected) {
      blockedReason = customModels.length > 0
        ? 'Style Tags are required for v6 models. Pick one of your custom models under Model to leave Style blank.'
        : 'Style Tags are required for v6 models.';
    }
  }

  const handleGenerate = useCallback(() => {
    if (!canGenerate) return;
    // draftId isn't editable here, so pass the caller's straight through --
    // dropping it meant finished clips were never linked to their vault draft.
    const modelPill = pillIdForGeneration(model, customName);
    onGenerate({ title, tags, negativeTags, lyrics, styleStrength, weirdness, makeInstrumental, vocalGender, maxMode, variety, durationSec, personalize, model, modelPill, draftId: initialPayload?.draftId ?? null });
  }, [canGenerate, onGenerate, title, tags, negativeTags, lyrics, styleStrength, weirdness, makeInstrumental, vocalGender, maxMode, variety, durationSec, personalize, model, customName, initialPayload]);

  const handleUpsample = useCallback(async () => {
    if (!onUpsample || !tags.trim() || isUpsampling) return;
    setIsUpsampling(true);
    try {
      const result = await onUpsample(tags);
      if (result) setTags(result);
    } finally {
      setIsUpsampling(false);
    }
  }, [onUpsample, tags, isUpsampling]);

  const handleCopyCode = useCallback(() => {
    const code = generateSunoConsoleCode(lyrics, tags, title, styleStrength, weirdness);
    copyToClipboard(code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  }, [lyrics, tags, title, styleStrength, weirdness]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Suno Prep"
      size="xl"
      footer={
        <div className="w-full space-y-2">
        {blockedReason && (
          <p className="text-[11px] text-amber-400" role="status" data-testid="send-blocked-reason">{blockedReason}</p>
        )}
        <div className="flex items-center justify-between w-full gap-3">
          <button
            onClick={handleCopyCode}
            className={`flex items-center gap-2 px-4 py-2 text-[10px] font-bold uppercase tracking-widest border rounded-lg transition-all ${
              codeCopied
                ? 'text-green-300 border-green-500 bg-green-950/50'
                : 'text-slate-400 border-slate-700 bg-slate-900/60 hover:border-amber-500 hover:text-amber-400'
            }`}
          >
            {codeCopied ? <Check size={14} /> : <Code size={14} />}
            {codeCopied ? 'Copied' : 'Copy Suno Code'}
          </button>

          {billing && (
            <div className="flex items-center gap-2 text-[10px] font-mono tabular-nums text-slate-400">
              <span className="text-amber-400 font-bold">~5 cr</span>
              <span className="text-slate-600">|</span>
              <span>{billing.credits.toLocaleString()} remaining</span>
              <span className="text-slate-600">|</span>
              <span>{billing.monthly_usage.toLocaleString()}/{billing.monthly_limit.toLocaleString()}</span>
              {billing.credits < 10 && (
                <span className="flex items-center gap-1 text-red-400 font-bold">
                  <AlertTriangle size={10} />
                  Low
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 border border-slate-700 rounded-lg hover:border-slate-500 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="flex items-center gap-2 px-6 py-2 text-[10px] font-bold uppercase tracking-widest bg-accent text-slate-950 rounded-lg hover:bg-amber-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Send size={14} />
              {isGenerating ? 'Sending...' : 'Send to Suno'}
            </button>
          </div>
        </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Title */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Song Title</label>
            <CharCount current={title.length} max={LIMITS.title} />
          </div>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="My Song Title"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:border-accent focus:outline-none transition-colors"
          />
        </div>

        {/* Style Tags */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                Style Tags{isCustomSelected && <span className="text-slate-600 normal-case"> (optional with a custom model)</span>}
              </label>
              {onUpsample && (
                <button
                  type="button"
                  onClick={handleUpsample}
                  disabled={isUpsampling || !tags.trim()}
                  className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-amber-400 bg-amber-950/30 border border-amber-800/40 rounded-md hover:bg-amber-900/40 hover:border-amber-600/50 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  title="AI-expand style tags (Suno Premier)"
                >
                  <Sparkles size={10} className={isUpsampling ? 'animate-spin' : ''} />
                  {isUpsampling ? 'Expanding...' : 'Upsample'}
                </button>
              )}
            </div>
            <CharCount current={tags.length} max={LIMITS.tags} />
          </div>
          <textarea
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder={isCustomSelected ? "Leave blank to use the custom model's own default style" : 'southern rock, blues, outlaw country, raw vocals...'}
            rows={2}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:border-accent focus:outline-none transition-colors resize-none"
          />
          {isCustomSelected && !tags.trim() && (
            <p className="mt-1 text-[10px] text-slate-500" data-testid="blank-style-note">
              Blank style: Suno will use {customName ? `${customName}'s` : "the custom model's"} own default style.
            </p>
          )}
        </div>

        {/* Negative Tags */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
              Exclude Tags <span className="text-slate-600 normal-case">(what to avoid)</span>
            </label>
            <CharCount current={negativeTags.length} max={LIMITS.negativeTags} />
          </div>
          <textarea
            value={negativeTags}
            onChange={(e) => setNegativeTags(e.target.value)}
            placeholder="auto-tune, EDM, synth pop..."
            rows={2}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:border-red-500/50 focus:outline-none transition-colors resize-none border-l-2 border-l-red-900"
          />
        </div>

        {/* Lyrics */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Lyrics</label>
            <div className="flex items-center gap-2">
              {lyrics.length > LIMITS.lyrics && (
                <button
                  type="button"
                  onClick={handleTrimToFit}
                  className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-400 border border-amber-700/50 rounded hover:bg-amber-950/40 transition-colors"
                >
                  <Scissors size={10} />
                  Trim to Fit
                </button>
              )}
              <CharCount current={lyrics.length} max={LIMITS.lyrics} />
            </div>
          </div>
          <textarea
            value={lyrics}
            onChange={(e) => { setLyrics(e.target.value); setTrimNote(null); }}
            placeholder="[Verse 1]&#10;Your lyrics here...&#10;&#10;[Chorus]&#10;..."
            rows={8}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:border-accent focus:outline-none transition-colors resize-y font-mono"
          />
          {trimNote && (
            <p className="mt-1 text-[10px] text-amber-400 font-mono">{trimNote}</p>
          )}
        </div>

        {/* Sliders + Instrumental */}
        <div className="bg-slate-950/60 border border-slate-800 rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
            <Sliders size={14} className="text-accent" />
            Generation Controls
          </div>

          {/* Model */}
          <div className="space-y-1.5">
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Model</div>
            <div role="radiogroup" aria-label="Suno model" className="flex gap-1.5">
              {SUNO_GENERATION_MODELS.map(m => {
                const on = model === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setModel(m.value)}
                    title={m.label}
                    className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                      on ? 'bg-accent/20 border-accent/70 text-accent' : 'border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {m.short}
                  </button>
                );
              })}
              {customModels.length > 0 && <span className="mx-1 self-stretch w-px bg-slate-700" aria-hidden="true" />}
              {customModels.map(c => {
                const on = model === c.mv;
                return (
                  <button
                    key={c.mv}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setModel(c.mv)}
                    title={`Your custom model (v6-based): ${c.name}`}
                    className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                      on ? 'bg-amber-500/20 border-amber-500/60 text-amber-300' : 'border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-500">
              Used when you press Generate. Suno has retired the older models, so only the v6 family and your custom models are offered.
              {customLoadFailed && <span className="text-amber-400"> Couldn't load your custom models.</span>}
            </p>
          </div>

          {/* Style Strength */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-[11px] font-bold uppercase tracking-widest">
              <span className="text-slate-400">Style Strength</span>
              <span className="text-accent font-mono">{styleStrength}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={styleStrength}
              onChange={(e) => setStyleStrength(parseInt(e.target.value))}
              className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <p className="text-[10px] text-slate-500">How closely Suno follows your style tags vs its own creativity</p>
          </div>

          {/* Weirdness */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-[11px] font-bold uppercase tracking-widest">
              <span className="text-slate-400">Weirdness</span>
              <span className="text-red-400 font-mono">{weirdness}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={weirdness}
              onChange={(e) => setWeirdness(parseInt(e.target.value))}
              className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-red-500"
            />
            <p className="text-[10px] text-slate-500">Level of sonic experimentalism and creative artifacts</p>
          </div>

          {/* Variety (Suno's aug_creativity: 0 Off, 1 Normal, 2 High, 3 Extra, 4 Max; confirmed from a 5-clip capture 2026-10-11) */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-[11px] font-bold uppercase tracking-widest">
              <span className="text-slate-400">Variety</span>
              <span className="text-sky-400 font-mono">{VARIETY_LABELS[variety] ?? 'Off'}</span>
            </div>
            <input
              type="range"
              min="0"
              max="4"
              step="1"
              value={variety}
              onChange={(e) => setVariety(parseInt(e.target.value))}
              className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-sky-500"
            />
            <p className="text-[10px] text-slate-500">Suno's Variety slider: how much the takes differ from each other. Off sends nothing.</p>
          </div>

          {/* Vocal Gender */}
          <div className="flex items-center justify-between pt-1">
            <div>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Vocal Gender</span>
              <p className="text-[10px] text-slate-500">Suno's own toggle. Click the selected one again to leave it to Suno.</p>
            </div>
            <div role="radiogroup" aria-label="Vocal gender" className="flex gap-1">
              {([['m', 'Male'], ['f', 'Female']] as const).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={vocalGender === v}
                  onClick={() => setVocalGender(vocalGender === v ? null : v)}
                  className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                    vocalGender === v ? 'bg-accent/20 border-accent/60 text-accent' : 'border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Duration: Auto sends nothing; Custom sends seconds (Suno's range 0:10-6:00, 5 s steps) */}
          <div className="space-y-1.5 pt-1">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Duration</span>
                <p className="text-[10px] text-slate-500">Auto lets Suno decide. Custom is a request: Suno ignores it when the lyrics are a full song (35 s asked, 3:56 made).</p>
              </div>
              <div role="radiogroup" aria-label="Duration" className="flex gap-1">
                {([['auto', 'Auto'], ['custom', 'Custom']] as const).map(([v, label]) => {
                  const on = (durationSec === null) === (v === 'auto');
                  return (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setDurationSec(v === 'auto' ? null : (durationSec ?? 180))}
                      className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                        on ? 'bg-accent/20 border-accent/60 text-accent' : 'border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            {durationSec !== null && (
              <div className="space-y-1.5">
                <div className="flex justify-end text-[11px] font-bold tracking-widest">
                  <span className="text-emerald-400 font-mono">{fmtDuration(durationSec)}</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="360"
                  step="5"
                  value={durationSec}
                  aria-label="Custom duration"
                  onChange={(e) => setDurationSec(parseInt(e.target.value))}
                  className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                />
              </div>
            )}
          </div>

          {/* Max Mode */}
          <div className="flex items-center justify-between pt-1">
            <div>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Max Mode</span>
              <p className="text-[10px] text-slate-500">Suno's own Max Mode toggle (Off sends nothing)</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={maxMode}
              aria-label="Max Mode"
              onClick={() => setMaxMode(!maxMode)}
              className="text-slate-400 hover:text-accent transition-colors"
            >
              {maxMode ? <ToggleRight size={28} className="text-accent" /> : <ToggleLeft size={28} />}
            </button>
          </div>

          {/* Personalize (My Taste) */}
          <div className="flex items-center justify-between pt-1">
            <div>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Personalize</span>
              <p className="text-[10px] text-slate-500">Suno's My Taste toggle (Off sends nothing)</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={personalize}
              aria-label="Personalize"
              onClick={() => setPersonalize(!personalize)}
              className="text-slate-400 hover:text-accent transition-colors"
            >
              {personalize ? <ToggleRight size={28} className="text-accent" /> : <ToggleLeft size={28} />}
            </button>
          </div>

          {/* Instrumental Toggle */}
          <div className="flex items-center justify-between pt-1">
            <div>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Instrumental Only</span>
              <p className="text-[10px] text-slate-500">Generate without vocals (lyrics will be ignored)</p>
            </div>
            <button
              type="button"
              onClick={() => setMakeInstrumental(!makeInstrumental)}
              className="text-slate-400 hover:text-accent transition-colors"
            >
              {makeInstrumental
                ? <ToggleRight size={28} className="text-accent" />
                : <ToggleLeft size={28} />
              }
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SunoPrepModal;
